/**
 * Shared React state for company-scoped detail pages that support:
 *   - Date-range filtering (year / date-range mode)
 *   - Company selector (for platform admins)
 *   - Month drill-down with per-day data
 *
 * Used by CompanyGHGPage and CompanyStatisticsPage to eliminate the block of
 * identical useState + useDateRangeFilter declarations that SonarCloud detects
 * as a code clone.
 */

import { useState, useRef } from 'react';
import { useDateRangeFilter } from './useDateRangeFilter';
import type { DateRangeFilterState } from './useDateRangeFilter';

export interface SelectedMonth {
  year: number;
  month: number;
  month_name: string;
}

export interface CompanyPageState<TData> extends DateRangeFilterState {
  data: TData | null;
  setData: (data: TData | null) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  selectedCompanyId: number | null;
  setSelectedCompanyId: (id: number | null) => void;
  selectedMonth: SelectedMonth | null;
  setSelectedMonth: (month: SelectedMonth | null) => void;
  dailyData: Array<any>;
  setDailyData: (data: Array<any>) => void;
  loadingDaily: boolean;
  setLoadingDaily: (loading: boolean) => void;
  dailyBreakdownRef: React.RefObject<HTMLDivElement | null>;
}

export function useCompanyPageState<TData>(): CompanyPageState<TData> {
  const dateRangeFilter = useDateRangeFilter();

  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<SelectedMonth | null>(null);
  const [dailyData, setDailyData] = useState<Array<any>>([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const dailyBreakdownRef = useRef<HTMLDivElement>(null);

  return {
    ...dateRangeFilter,
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedMonth,
    setSelectedMonth,
    dailyData,
    setDailyData,
    loadingDaily,
    setLoadingDaily,
    dailyBreakdownRef,
  };
}
